import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  UserIcon,
  BuildingStorefrontIcon,
  TicketIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';

const TransactionList = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    type: 'all',
    minAmount: '',
    maxAmount: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [reservationDetails, setReservationDetails] = useState({});
  const [bookerDetails, setBookerDetails] = useState({});

  const [sortConfig, setSortConfig] = useState({
    key: 'createTime',
    direction: 'desc'
  });

  const transactionTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'Partner-pass', label: 'Partner Pass' },
    { value: 'reservation', label: 'Reservation' },
    { value: 'extend', label: 'Extend' },
    { value: 'Coupon', label: 'Coupon' }
  ];

  // Helper functions
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    const numericValue = extractNumericValue(amount);
    if (isNaN(numericValue) || numericValue === 0) return '₹0.00';
    return `₹${numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const extractNumericValue = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    if (typeof value === 'string') {
      // Remove currency symbols, commas, and spaces, then parse
      const cleaned = value.replace(/[₹$,]/g, '').trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      
      // Get all transactions
      const transactionsQuery = query(collection(db, 'transaction'), orderBy('createTime', 'desc'));
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const transactionsData = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTransactions(transactionsData);
      
      // Load related reservation and booker details
      await loadRelatedDetails(transactionsData);
      
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedDetails = async (transactionsData) => {
    const db = getFirestore();
    const reservationDetailsMap = {};
    const bookerDetailsMap = {};

    // Extract unique reservation IDs and booker IDs
    const reservationIds = new Set();
    const bookerIds = new Set();

    transactionsData.forEach(transaction => {
      if (transaction.reservation) {
        reservationIds.add(transaction.reservation);
      }
      if (transaction.bookerid) {
        bookerIds.add(transaction.bookerid);
      }
    });

    // Load reservation details
    for (const reservationId of reservationIds) {
      try {
        const reservationDoc = await getDoc(doc(db, 'reservations', reservationId));
        if (reservationDoc.exists()) {
          reservationDetailsMap[reservationId] = reservationDoc.data();
        }
      } catch (err) {
        console.error(`Error loading reservation ${reservationId}:`, err);
      }
    }

    // Load booker details
    for (const bookerId of bookerIds) {
      try {
        const bookerDoc = await getDoc(doc(db, 'users', bookerId));
        if (bookerDoc.exists()) {
          bookerDetailsMap[bookerId] = bookerDoc.data();
        }
      } catch (err) {
        console.error(`Error loading booker ${bookerId}:`, err);
      }
    }

    setReservationDetails(reservationDetailsMap);
    setBookerDetails(bookerDetailsMap);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      type: 'all',
      minAmount: '',
      maxAmount: ''
    });
    setCurrentPage(1);
  };

  // Filter transactions by search and filters
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    // Apply type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(transaction => transaction.type === filters.type);
    }

    // Apply date filters
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(transaction => {
        const transactionDate = transaction.createTime?.toDate ? 
          transaction.createTime.toDate() : new Date(transaction.createTime);
        return transactionDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(transaction => {
        const transactionDate = transaction.createTime?.toDate ? 
          transaction.createTime.toDate() : new Date(transaction.createTime);
        return transactionDate <= endDate;
      });
    }

    // Apply amount filters (using total field)
    if (filters.minAmount) {
      const minAmount = parseFloat(filters.minAmount);
      filtered = filtered.filter(transaction => extractNumericValue(transaction.total) >= minAmount);
    }

    if (filters.maxAmount) {
      const maxAmount = parseFloat(filters.maxAmount);
      filtered = filtered.filter(transaction => extractNumericValue(transaction.total) <= maxAmount);
    }

    // Apply search filter
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(transaction => {
        const reservation = reservationDetails[transaction.reservation];
        const booker = bookerDetails[transaction.bookerid];
        
        return (
          (transaction.id || '').toLowerCase().includes(s) ||
          (transaction.type || '').toLowerCase().includes(s) ||
          (transaction.reservation || '').toLowerCase().includes(s) ||
          (reservation?.reservationid || '').toLowerCase().includes(s) ||
          (reservation?.vehicleData?.number || '').toLowerCase().includes(s) ||
          (transaction.ownerName || '').toLowerCase().includes(s) ||
          (transaction.phone || '').toLowerCase().includes(s)
        );
      });
    }

    return filtered;
  }, [transactions, search, filters, reservationDetails, bookerDetails]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return filteredTransactions.reduce((totals, transaction) => {
      // Debug logging for Coupon transactions
      if (transaction.type === 'Coupon') {
        console.log('Coupon transaction:', {
          id: transaction.id,
          cgst: transaction.cgst,
          sgst: transaction.sgst,
          cgstType: typeof transaction.cgst,
          sgstType: typeof transaction.sgst
        });
      }
      
      totals.price += extractNumericValue(transaction.price);
      totals.parkingfee += extractNumericValue(transaction.parkingfee);
      totals.cgst += extractNumericValue(transaction.cgst);
      totals.sgst += extractNumericValue(transaction.sgst);
      totals.total += extractNumericValue(transaction.total);
      return totals;
    }, { price: 0, parkingfee: 0, cgst: 0, sgst: 0, total: 0 });
  }, [filteredTransactions]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUpIcon className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 text-blue-600" />
      : <ChevronDownIcon className="w-4 h-4 text-blue-600" />;
  };

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Handle timestamp conversion for date sorting
    if (sortConfig.key === 'createTime') {
      aValue = aValue?.toDate ? aValue.toDate() : new Date(aValue);
      bValue = bValue?.toDate ? bValue.toDate() : new Date(bValue);
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedTransactions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentTransactions = sortedTransactions.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'reservation':
        return <CalendarIcon className="h-4 w-4" />;
      case 'Partner-pass':
        return <TicketIcon className="h-4 w-4" />;
      case 'extend':
        return <ClockIcon className="h-4 w-4" />;
      case 'Coupon':
        return <CreditCardIcon className="h-4 w-4" />;
      default:
        return <CurrencyRupeeIcon className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'reservation':
        return 'bg-blue-100 text-blue-800';
      case 'Partner-pass':
        return 'bg-purple-100 text-purple-800';
      case 'extend':
        return 'bg-orange-100 text-orange-800';
      case 'Coupon':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">Transaction History</h1>
            </div>
            <div className="text-sm text-gray-500">
              {filteredTransactions.length} transactions found
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {/* Summary Cards */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{filteredTransactions.length}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Total Price</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(summaryTotals.price)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Total Parking Fee</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryTotals.parkingfee)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Total CGST</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(summaryTotals.cgst)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Total SGST</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(summaryTotals.sgst)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Grand Total</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryTotals.total)}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                  <input
                    type="text"
                    placeholder="Search transactions by ID, type, reservation ID, vehicle number, owner name, or phone..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    {transactionTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
                  <input
                    type="number"
                    placeholder="∞"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Transactions Cards */}
        <div className="bg-white shadow rounded-lg">
          {error ? (
            <div className="p-6 text-center text-red-600">{error}</div>
          ) : (
            <>
              <div className="p-6">
                <div className="grid gap-4">
                  {currentTransactions.map((transaction) => {
                    const reservation = reservationDetails[transaction.reservation];
                    
                    return (
                      <div key={transaction.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        {/* Top Row - Icon, Transaction ID, Date, Owner, Phone, Total */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              <div className={`p-2 rounded-lg ${getTypeColor(transaction.type).replace('text-', 'bg-').replace('bg-gray-100', 'bg-gray-200')}`}>
                                {getTypeIcon(transaction.type)}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Transaction ID</p>
                              <p className="text-sm font-mono text-blue-600">{transaction.id}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Date</p>
                              <p className="text-sm text-gray-900">{formatDate(transaction.createTime)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Owner</p>
                              <p className="text-sm text-gray-900">{transaction.ownerName || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Phone</p>
                              <p className="text-sm text-gray-900">{transaction.phone || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-medium text-gray-500">Total</p>
                            <p className="text-lg font-bold text-green-600">{formatCurrency(transaction.total)}</p>
                          </div>
                        </div>
                        
                        {/* Bottom Row - Type, Price, Parking Fee, Reservation, Vehicle */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-6">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Type</p>
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                                {getTypeIcon(transaction.type)}
                                <span className="ml-1">{transaction.type}</span>
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Price</p>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.price)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Parking Fee</p>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.parkingfee)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">CGST</p>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.cgst)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">SGST</p>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.sgst)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Reservation</p>
                              <p className="text-sm text-gray-900">
                                {transaction.reservation ? (
                                  <span className="font-mono text-blue-600">
                                    {reservation?.reservationid || transaction.reservation}
                                  </span>
                                ) : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Vehicle</p>
                              <p className="text-sm text-gray-900">{reservation?.vehicleData?.number || 'N/A'}</p>
                            </div>
                          </div>
                          <div>
                            {transaction.reservation && (
                              <button
                                onClick={() => navigate(`/reservation-detail/${transaction.reservation}`)}
                                className="text-blue-600 hover:text-blue-900 flex items-center text-sm"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                View Details
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              {sortedTransactions.length > 0 && (
                <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow-sm">
                  <div className="flex items-center space-x-2 mb-3 sm:mb-0">
                    <span className="text-sm text-gray-700">Rows per page:</span>
                    <select 
                      value={rowsPerPage} 
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      Showing {startIndex + 1} to {Math.min(endIndex, sortedTransactions.length)} of {sortedTransactions.length} results
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionList; 